/* This component layout is base for subnavigation component so if this layout changes subnavigation should change too! */
import {
    Box,
    Container,
    Divider,
    Flex,
    HStack,
    IconButton,
    Skeleton,
    Stack,
    Text,
    useDisclosure,
    Button
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getAssetUrl} from 'pwa-kit-react-sdk/ssr/universal/utils'
import React, {useMemo, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {ArrowLeftRound2Icon, ArrowRightRound2Icon, PlayIcon} from '../../../components/icons'
import ImageGallery from '../../../components/image-gallery'
import ImageGallerySkeleton from '../../../components/image-gallery/skeleton'
import StyledContent from '../../../components/styled-content'
import BVProductRating from '../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../components/bazaarvoice/utils'
import {getBVReviewProductIds} from '../../../utils/product-utils'
import {MAIN_IMAGES_IMAGE_TYPE} from '../../../constants'
import {useProduct} from '../../../hooks'
import useElementScreenPosition from '../../../hooks/use-element-screen-position'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import PsdpCta from './psdp-cta'
import CockpitOrderSection from './cockpit-order-section'
import CockpitTags from './cockpit-tags'
import ProductSpecialOffers from '../../../components/special-offer/product-special-offers'
import ProductSetTile from '../../../components/product-set-tile'
import ProductSetCockpitTotal from './product-set-cockpit-total'
import {EngravingModal, useEngravingModal} from '../../../hooks/use-engraving-modal'
import {cloneDeep} from 'lodash'
import ProductSetModal from './product-set-modal'
import useMultiSite from '../../../hooks/use-multi-site'
import {trackPsdpClickProductTile, trackPsdpSwipeProductTile} from '../../../tracking/analytics'

const ProductSetCockpit = ({
    product,
    promotions,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    showZoomModal,
    onHotspotClick,
    onMasterChange,
    onVariantChange,
    onRatingsClick,
    ...props
}) => {
    const {site} = useMultiSite()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const engravingModal = useEngravingModal()
    const [modalSliderIndex, setModalSliderIndex] = useState(0)
    const configureRef = useRef()

    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])

    const {setRef: setHeadingRef} = useElementScreenPosition()

    const {videos, variationParams, hotspots, isEngravable} = useProduct(product)

    const [mainGalleryImages, mainHotspots] = useMemo(
        () => [
            [
                ...(product?.c_extraMainImage
                    ? [{disBaseLink: product?.c_extraMainImage, link: product?.c_extraMainImage}]
                    : []),
                ...(findImageGroupBy(product?.imageGroups, {
                    viewType: MAIN_IMAGES_IMAGE_TYPE,
                    selectedVariationAttributes: variationParams
                })?.images || []),
                ...(product?.c_extraMainImage2
                    ? [{disBaseLink: product?.c_extraMainImage2, link: product?.c_extraMainImage2}]
                    : []),
                ...(videos || [])
            ],
            hotspots?.[MAIN_IMAGES_IMAGE_TYPE]
        ],
        [
            product?.c_extraMainImage,
            product?.c_extraMainImage2,
            product?.imageGroups,
            variationParams,
            videos,
            hotspots
        ]
    )

    const stickyProps = {
        position: {lg: 'sticky'},
        top: 8
    }

    const leftSidebarSizeProps = {
        flexBasis: {
            lg: 'calc(25% + 0.5rem)',
            xl: 'calc(25% + 0.875rem)',
            '2xl': 'calc(25% + 0.75rem)'
        },
        minWidth: 0
    }

    const rightSidebarSizeProps = cloneDeep(leftSidebarSizeProps)
    rightSidebarSizeProps.flexBasis.lg = '32.113%'

    const onPsdpCtaClick = () => {
        configureRef?.current?.scrollIntoView({behavior: 'smooth'})
    }

    return (
        <>
            <Container layerStyle="page" variant="no-padding">
                <Flex
                    ref={setHeadingRef}
                    direction={{base: 'column', lg: 'row'}}
                    gap={{base: 6, '2xl': 8}}
                    alignItems={{lg: 'flex-start'}}
                >
                    {/* Product image - STOK-6322 */}
                    <Box flex={1} {...stickyProps}>
                        {!isVariantLoading ? (
                            <ImageGallery
                                variant="cockpit"
                                items={mainGalleryImages}
                                hotspots={mainHotspots}
                                cursor={`url('${encodeURI(
                                    getAssetUrl('static/ico/zoom.svg')
                                )}'),zoom-in`}
                                navLeftIcon={<ArrowLeftRound2Icon boxSize={8} />}
                                navRightIcon={<ArrowRightRound2Icon boxSize={8} />}
                                preload
                                showChildrenIn="overlay"
                                showPagination
                                imageProps={{
                                    widths: {base: '100vw', lg: '50vw'}
                                }}
                                onClick={
                                    showZoomModal &&
                                    ((_, image, index) => {
                                        showZoomModal({image, index})
                                    })
                                }
                                onChange={() => trackPsdpSwipeProductTile(product)}
                                onHotspotClick={onHotspotClick}
                            >
                                <Flex justifyItems="space-between">
                                    <HStack spacing={2} pointerEvents="auto">
                                        {videos && videos?.length && (
                                            <IconButton
                                                variant="pillOnTile"
                                                aria-label={formatMessage({
                                                    id: 'product_detail.button.assistive_msg.video',
                                                    defaultMessage: 'Video'
                                                })}
                                                onClick={() => {
                                                    showZoomModal({tab: 1})
                                                }}
                                            >
                                                <PlayIcon boxSize={6} />
                                            </IconButton>
                                        )}
                                    </HStack>
                                </Flex>
                            </ImageGallery>
                        ) : (
                            <ImageGallerySkeleton />
                        )}
                    </Box>

                    {/* mobile top / desktop left column */}
                    <Stack
                        spacing={6}
                        order={{lg: -1}}
                        px={{base: 4, lg: 0}}
                        {...stickyProps}
                        {...leftSidebarSizeProps}
                    >
                        {/* Headline */}
                        <Stack spacing={2}>
                            <Flex direction="row" flexWrap="wrap" gap={4}>
                                {hasProductRating && (
                                    <BVProductRating
                                        pid={getBVReviewProductIds(product)}
                                        variant="cockpit"
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                                <CockpitTags product={product} />
                            </Flex>

                            {/* Title */}
                            <Text as={'h1'} textStyle="Regular 2XLarge">
                                {product?.name}
                            </Text>
                        </Stack>

                        {/* highlights */}
                        <Stack spacing={2}>
                            <Text as={'h2'} textStyle="Semi Bold Small">
                                {product?.c_slogan}
                            </Text>
                            <StyledContent content={product?.c_USP} textStyle="List Regular" />
                        </Stack>

                        {/* Special offer on desktop */}
                        <Box display={{base: 'none', lg: 'block'}}>
                            {!isPromotionsLoading && (
                                <ProductSpecialOffers product={product} promotions={promotions} />
                            )}
                        </Box>
                    </Stack>

                    {/* mobile bottom / desktop right column */}
                    <Stack
                        data-testid={'product-set-cockpit-right-column'}
                        spacing={4}
                        px={{base: 4, lg: 0}}
                        scrollMarginTop={24}
                        {...stickyProps}
                        {...rightSidebarSizeProps}
                        ref={configureRef}
                    >
                        {/* Frame 3319 */}
                        <Stack spacing={4}>
                            {/*Set Total*/}
                            <ProductSetCockpitTotal
                                product={product}
                                display={{base: 'none', lg: 'flex'}}
                            />

                            <Divider
                                display={{base: 'none', lg: 'block'}}
                                borderColor="stokkeGray.dark"
                            />

                            <Stack spacing={2}>
                                <Text as={'h2'} textStyle="Regular XLarge">
                                    <FormattedMessage
                                        id="product_set_detail.cockpit.title"
                                        defaultMessage="Make your set unique!"
                                    />
                                </Text>
                                <Text textStyle="Regular Small">
                                    <FormattedMessage
                                        id="product_set_detail.cockpit.description"
                                        defaultMessage="Choose different combinations by clicking on the tile."
                                    />
                                </Text>
                            </Stack>

                            <ProductSetModal
                                products={product.setProducts}
                                isOpen={isOpen}
                                onClose={onClose}
                                initialIndex={modalSliderIndex}
                                {...props}
                            />
                            {product.setProducts.map((childProduct, index) => (
                                <Box
                                    key={childProduct.id + '_' + index}
                                    data-testid="child-product"
                                >
                                    <ProductSetTile
                                        onOpen={() => {
                                            setModalSliderIndex(index)
                                            onOpen()
                                            trackPsdpClickProductTile(childProduct)
                                        }}
                                        productSetIndex={index}
                                        product={childProduct}
                                        dynamicImageProps={{
                                            widths: ['70vw', '70vw', '40vw', '30vw']
                                        }}
                                        {...props}
                                    />
                                </Box>
                            ))}
                        </Stack>

                        <Stack spacing={{base: 4, lg: 6}} pt={{lg: 2}}>
                            {!site?.isContentOnlySite && isEngravable && (
                                <Button variant="outline" size="lg" onClick={engravingModal.onOpen}>
                                    <FormattedMessage
                                        id="product_set_detail.cockpit.add_all_to_basket_incl_engraving"
                                        defaultMessage="Add all to basket incl. engraving"
                                    />
                                </Button>
                            )}

                            <ProductSetCockpitTotal product={product} />
                        </Stack>

                        {/* Special offer on mobile */}
                        <Skeleton isLoaded={!isPromotionsLoading && !isVariantLoading}>
                            <Box display={{base: 'block', lg: 'none'}}>
                                <ProductSpecialOffers product={product} promotions={promotions} />
                            </Box>
                        </Skeleton>

                        {/* Bottom */}
                        <CockpitOrderSection
                            product={product}
                            promotions={promotions}
                            isPromotionsLoading={isPromotionsLoading}
                            isVariantLoading={isVariantLoading}
                            {...props}
                        />
                    </Stack>
                    <PsdpCta
                        isPromotionsLoading={isPromotionsLoading}
                        isVariantLoading={isVariantLoading}
                        product={product}
                        clickHandler={onPsdpCtaClick}
                        {...props}
                    />
                </Flex>
            </Container>
            {isEngravable && (
                <EngravingModal
                    {...engravingModal}
                    isPromotionsLoading={isPromotionsLoading}
                    isVariantLoading={isVariantLoading}
                    product={product}
                    promotions={promotions}
                />
            )}
        </>
    )
}

ProductSetCockpit.propTypes = {
    product: PropTypes.object,
    promotions: PropTypes.array,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    showZoomModal: PropTypes.func,
    notifyMe: PropTypes.func,
    onHotspotClick: PropTypes.func,
    onMasterChange: PropTypes.func,
    onRatingsClick: PropTypes.func,
    onVariantChange: PropTypes.func
}

export default ProductSetCockpit
